import React from 'react'
import { Flex, Text } from '@fundamentals'
import { Card } from '@common/material'
import { localizedStrings } from '@core/strings'
import {
  Cancel,
  CheckCircle,
  FiberManualRecord,
  RemoveCircle,
} from '@mui/icons-material'
import {
  TaskTemplatePriorityType,
  TaskTemplateProperties,
} from '@core/entities/task/TaskTemplateProperties'
import capitalize from 'lodash/capitalize'
import {
  ChecklistInspectionStats,
  InspectionOutcome,
  inspectionOutcomeLabels,
} from '@core/entities/checklist/Checklist/types'
import { Divider } from '@common/material'

export const InspectionQuestionsStats = ({
  stats,
  variant = 'templateForm',
}: {
  stats: ChecklistInspectionStats
  variant?: 'templateForm' | 'checklistDetails'
}) => {
  const templateFormCardSx = {
    bgcolor: 'grey.50',
    border: 'none',
  }

  if (!stats) return null

  return (
    <Flex
      gap={2}
      justifyContent='space-between'
      mt={2}
      flexDirection={variant === 'checklistDetails' ? 'column' : 'row'}
    >
      <Card
        sx={{
          flex: 1,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          ...(variant === 'templateForm' ? templateFormCardSx : {}),
        }}
      >
        <Flex gap={1} justifyContent='space-between' alignItems='center'>
          <Text
            sx={{ fontSize: '16px' }}
            variant='h6'
            color='text.primary'
            gutterBottom
          >
            {localizedStrings.inspectionItems}
          </Text>
          <Text
            sx={{ fontSize: '16px', fontVariantNumeric: 'tabular-nums' }}
            variant='h6'
            color='text.primary'
            gutterBottom
          >
            {stats.inspectionItems.total}
          </Text>
        </Flex>
        {variant === 'checklistDetails' && <Divider />}
        <Flex flexDirection='column' gap={0.5} mt={2}>
          {Object.entries(stats.inspectionItems.byOutcome).map(
            ([outcomeKey, count]) => (
              <Flex alignItems='center' gap={1} key={outcomeKey}>
                {outcomeKey === 'pass' && (
                  <CheckCircle
                    fontSize='small'
                    sx={{ color: 'success.main' }}
                  />
                )}
                {outcomeKey === 'fail' && (
                  <Cancel fontSize='small' sx={{ color: 'error.main' }} />
                )}
                {outcomeKey === 'na' && (
                  <RemoveCircle fontSize='small' sx={{ color: 'grey.500' }} />
                )}
                <Text fontSize={14}>
                  {inspectionOutcomeLabels[outcomeKey as InspectionOutcome]}
                </Text>
                <Text
                  ml='auto'
                  fontSize={14}
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {count}
                </Text>
              </Flex>
            ),
          )}
        </Flex>
      </Card>
      <Card
        sx={{
          flex: 1,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          ...(variant === 'templateForm' ? templateFormCardSx : {}),
        }}
      >
        <Flex gap={1} justifyContent='space-between' alignItems='center'>
          <Text
            sx={{ fontSize: '16px' }}
            variant='h6'
            color='text.primary'
            gutterBottom
          >
            {localizedStrings.tasksRaised}
          </Text>
          <Text
            sx={{ fontSize: '16px', fontVariantNumeric: 'tabular-nums' }}
            variant='h6'
            color='text.primary'
            gutterBottom
          >
            {stats.raisedTasks.total}
          </Text>
        </Flex>
        {variant === 'checklistDetails' && <Divider />}
        <Flex flexDirection='column' gap={0.5} mt={2}>
          {Object.entries(stats.raisedTasks.byPriority).map(
            ([priorityKey, count]) => (
              <Flex alignItems='center' gap={1} key={priorityKey}>
                <FiberManualRecord
                  sx={{
                    fontSize: 14,
                    color: TaskTemplateProperties.getColorForPriority(
                      priorityKey.toUpperCase() as TaskTemplatePriorityType,
                    ),
                  }}
                />
                <Text fontSize={14}>{capitalize(priorityKey)}</Text>
                <Text
                  ml='auto'
                  fontSize={14}
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {count}
                </Text>
              </Flex>
            ),
          )}
        </Flex>
        {!!stats.unresolvedTasks && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Flex justifyContent='space-between' alignItems='center'>
              <Text
                showTooltip
                sx={{ fontSize: 14, mb: 0 }}
                variant='h6'
                color='text.primary'
                gutterBottom
              >
                {localizedStrings.unresolved}
              </Text>
              <Text
                showTooltip
                sx={{ fontSize: 14, mb: 0, fontVariantNumeric: 'tabular-nums' }}
                variant='h6'
                color='text.primary'
                gutterBottom
              >
                {stats.unresolvedTasks?.total}
              </Text>
            </Flex>
          </>
        )}
      </Card>
    </Flex>
  )
}
